import { CheckCircledIcon } from '@radix-ui/react-icons'
import { Alert, Skeleton, Space, Steps } from 'antd'
import { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import { DocumentNavigationSidebarContainer } from './components/DocumentNavigationSidebarContainer'
import { ResizableThreePanelLayout } from './components/ResizableThreePanelLayout'
import {
  DocumentDataProvider,
  useDocumentData,
} from './document/DocumentDataContext'
import { DocumentUIProvider } from './document/DocumentUIContext'
import { EmbeddedDocumentPanel } from './document/EmbeddedDocumentPanel'
import {
  LibraDocumentProvider,
  useLibraDocument,
} from './document/LibraDocumentContext'
import { LibraTicket } from './ticket/libraTicketsApi'
import { TicketStepContextProvider } from './ticket/TicketStateContext'
import { useTicketUpdate } from './ticket/TicketUpdateContext'
import { useWorkflow } from './WorkflowContext'
import { WorkflowStepContainer } from './WorkflowStepContainer'

export const LibraView: FC<{
  ticket: LibraTicket
}> = ({ ticket }) => {
  const unitId = ticket.tags.unit as string | undefined
  const workflow = useWorkflow()
  const maxNavigableIndex = useMemo(() => {
    return workflow.stepIndices.get(ticket.stepId) ?? -1
  }, [ticket.stepId, workflow.stepIndices])
  const [activeStepIndex, setActiveStepIndex] = useState<number>(
    maxNavigableIndex ?? 0
  )
  useEffect(() => {
    setActiveStepIndex(maxNavigableIndex)
  }, [maxNavigableIndex])
  const activeStep = workflow.steps[activeStepIndex]

  const stepItems = useMemo(
    () =>
      workflow.steps.map((step, index) => ({
        key: step.name ?? `step-${index}`,
        title: step.name,
        status:
          index === activeStepIndex
            ? ('process' as const)
            : index < activeStepIndex
              ? ('finish' as const)
              : ('wait' as const),
        disabled: index > maxNavigableIndex,
      })),
    [activeStepIndex, maxNavigableIndex, workflow.steps]
  )

  const { isLoading } = useTicketUpdate()

  const stepContent = (
    <FullHeightSpace orientation='vertical' size='middle'>
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : (
        <>
          <StepsWrapper>
            <Steps
              current={activeStepIndex}
              onChange={(index) => {
                const step = workflow.steps[index]
                if (!step) {
                  console.warn('No step found for index', index)
                  return
                }
                setActiveStepIndex(index)
              }}
              items={stepItems}
            />
          </StepsWrapper>
          {ticket.workflowComplete && (
            <WorkflowCompleteAlert
              message={
                <WorkflowCompleteAlerContent>
                  <CheckCircledIcon /> Workflow complete
                </WorkflowCompleteAlerContent>
              }
              type='success'
            />
          )}
          <StepContent>
            {activeStep ? (
              <TicketStepContextProvider
                ticket={ticket}
                activeStepId={activeStep.id}>
                <WorkflowStepContainer
                  componentName={activeStep.component}
                  config={activeStep.config}
                />
              </TicketStepContextProvider>
            ) : null}
          </StepContent>
        </>
      )}
    </FullHeightSpace>
  )

  return (
    <LibraDocumentProvider unitId={unitId}>
      <DocumentDataProvider unitId={unitId}>
        <DocumentUIProvider>
          <LibraViewContent stepContent={stepContent} unitId={unitId || ''} />
        </DocumentUIProvider>
      </DocumentDataProvider>
    </LibraDocumentProvider>
  )
}

const LibraViewContent: FC<{
  stepContent: React.ReactNode
  unitId: string
}> = ({ stepContent, unitId }) => {
  const { totalDocuments } = useDocumentData()
  const { selectedDocumentId } = useLibraDocument()

  // Show PDF viewer only if there are documents and one is selected
  const showPdfViewer = totalDocuments > 0 && selectedDocumentId

  if (showPdfViewer) {
    return (
      <ResizableThreePanelLayout
        leftPanel={
          <DocumentNavigationSidebarContainer
            unitId={unitId}
            showUploader={true}
          />
        }
        centerPanel={<EmbeddedDocumentPanel />}
        rightPanel={stepContent}
        defaultLeftWidth={220}
        defaultCenterWidth={400}
        minPanelWidth={180}
        collapsible={true}
      />
    )
  }

  // Two-panel layout when no documents
  return (
    <TwoPanelLayout>
      <NavigatorOnlyPanel>
        <DocumentNavigationSidebarContainer
          unitId={unitId}
          showUploader={true}
        />
      </NavigatorOnlyPanel>
      <StepsOnlyPanel>{stepContent}</StepsOnlyPanel>
    </TwoPanelLayout>
  )
}

const FullHeightSpace = styled(Space)`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  > .ant-space-item {
    display: flex;
    width: 100%;
  }

  > .ant-space-item:last-child {
    flex: 1;
    min-height: 0;
  }
`

const StepsWrapper = styled.div`
  flex: 0 0 auto;
  width: 100%;
`

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  flex: 1;
  overflow-y: auto;
`

const WorkflowCompleteAlert = styled(Alert)`
  width: 100%;
`

const WorkflowCompleteAlerContent = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const TwoPanelLayout = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
`

const NavigatorOnlyPanel = styled.div`
  width: 220px;
  min-width: 180px;
  height: 100%;
  border-right: 1px solid ${({ theme }) => theme.colors.grayscale[500]};
  background: ${({ theme }) => theme.forecast.background1};
`

const StepsOnlyPanel = styled.div`
  flex: 1;
  height: 100%;
  min-width: 0;
  background: ${({ theme }) => theme.forecast.background1};
`
